import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Upload, FileText, X, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const FileUpload = ({ onFileUploaded, currentFileId = null, currentFileName = null, testIdPrefix = 'file' }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(
    currentFileId ? { id: currentFileId, original_filename: currentFileName || 'Attached file' } : null
  );
  const fileInputRef = useRef(null);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File too large (max 2MB)');
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await axios.post(`${API}/api/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadedFile(data);
      onFileUploaded && onFileUploaded(data);
      toast.success('File uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemove = () => {
    setUploadedFile(null);
    onFileUploaded && onFileUploaded(null);
  };

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-upload-wrapper`}>
      {uploadedFile ? (
        <div className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-md" data-testid={`${testIdPrefix}-uploaded`}>
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-emerald-600" />
            <FileText className="w-4 h-4 text-slate-500" />
            <span className="text-sm text-slate-700 truncate max-w-xs">{uploadedFile.original_filename}</span>
          </div>
          <div className="flex gap-2">
            <a
              href={`${API}/api/files/${uploadedFile.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-600 hover:text-blue-700 underline"
              data-testid={`${testIdPrefix}-view-link`}
            >
              View
            </a>
            <button
              type="button"
              onClick={handleRemove}
              className="text-slate-400 hover:text-red-500"
              data-testid={`${testIdPrefix}-remove-button`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      ) : (
        <div>
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileChange}
            className="hidden"
            accept="image/*,.pdf,.doc,.docx"
            data-testid={`${testIdPrefix}-input`}
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="w-full"
            data-testid={`${testIdPrefix}-upload-button`}
          >
            <Upload className="w-4 h-4 mr-2" />
            {uploading ? 'Uploading...' : 'Upload File'}
          </Button>
          <p className="text-xs text-slate-500 mt-1">Max 2MB. Images, PDF, DOC supported.</p>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
